import { Menu } from '@mantine/core';
import {
  IconBuildingStore,
  IconCheck,
  IconChevronDown,
  IconMap2,
  IconMapPin,
  IconStack2,
  IconWorld,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSession, type ScopeChoice } from '../session';
import { toast } from '../toast';
import { useIsMobile } from '../useIsMobile';
import classes from './shell.module.css';

function ScopeIcon({ kind }: { kind: ScopeChoice['kind'] }) {
  const props = { size: 18, stroke: 2, className: classes.mutedIcon };
  switch (kind) {
    case 'all':
      return <IconWorld {...props} />;
    case 'region':
      return <IconMap2 {...props} />;
    case 'group':
      return <IconStack2 {...props} />;
    default:
      return <IconBuildingStore {...props} />;
  }
}

/**
 * Branch / region picker in the top bar. Only a control when the person covers more
 * than one branch; otherwise a static label. The choice applies app-wide.
 */
export function ScopeSwitcher() {
  const { scopes, scope, setScope } = useSession();
  const isMobile = useIsMobile();
  const [opened, setOpened] = useState(false);

  if (scopes.length <= 1) {
    return (
      <span className={classes.scopeStatic}>
        <IconMapPin size={16} stroke={2} className={classes.yellowIcon} />
        {scope.label}
      </span>
    );
  }

  const pick = (choice: ScopeChoice) => {
    setScope(choice.id);
    toast.info(`Showing ${choice.label}`, 'Pages with a branch or region picker start from here.');
  };

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      width={isMobile ? 'calc(100vw - 16px)' : 360}
      position="bottom-start"
      offset={4}
      shadow="md"
      withinPortal
    >
      <Menu.Target>
        <button
          type="button"
          className={classes.scopeButton}
          data-open={opened || undefined}
          aria-label={`Showing data for ${scope.label}. Change branch or region`}
        >
          <IconMapPin size={16} stroke={2} className={classes.yellowIcon} />
          <span className={classes.scopeLabel}>{scope.label}</span>
          <IconChevronDown size={16} stroke={2} className={classes.mutedIcon} />
        </button>
      </Menu.Target>
      <Menu.Dropdown aria-label="Choose branch or region" mah="70vh" style={{ overflowY: 'auto' }}>
        <Menu.Label>Show data for</Menu.Label>
        {scopes.map((choice) => {
          const selected = choice.id === scope.id;
          return (
            <Menu.Item
              key={choice.id}
              className={classes.scopeOption}
              data-sub={choice.within ? true : undefined}
              data-depth={choice.depth === 2 ? 2 : undefined}
              data-selected={selected || undefined}
              fw={choice.group ? 600 : 400}
              leftSection={<ScopeIcon kind={choice.kind} />}
              rightSection={
                selected ? <IconCheck size={16} color="var(--a-accent-text)" /> : undefined
              }
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => pick(choice)}
            >
              {choice.label}
            </Menu.Item>
          );
        })}
        <div className={classes.scopeFooter}>
          Home and every page with a branch or region picker start from this choice.
        </div>
      </Menu.Dropdown>
    </Menu>
  );
}
